import { SxProps, Theme } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Box } from 'src/components/Box';
import { Button, ButtonProps } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { NonEmptyArray } from 'src/types/utilityTypes';

interface ButtonPropsWithLabel extends ButtonProps {
  label: string;
}

interface DialogProps {
  open: boolean;
  title?: string;
  actions: React.ReactNode | NonEmptyArray<ButtonPropsWithLabel>;
  noWrap?: boolean;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  onClose?: () => void;
}

export const ConfirmDialog: React.FC<DialogProps> = ({
  open,
  title,
  actions,
  noWrap = false,
  sx,
  children,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      title={title}
      sx={sx}
      onClose={onClose}
      actions={
        <>
          {Array.isArray(actions) && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              spacing={1}
              sx={{ width: '100%' }}
            >
              {actions.map((buttonProps, i) => (
                <Button
                  key={i}
                  {...buttonProps}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    ...(buttonProps.sx || {}),
                  }}
                >
                  {buttonProps.children || buttonProps.label}
                </Button>
              ))}
            </Stack>
          )}
          {!Array.isArray(actions) && actions}
        </>
      }
      noWrap={noWrap}
      titleAlignment="center"
    >
      <Box sx={{ textAlign: 'center' }}>
        {typeof children === 'string' ? (
          <HtmlTypography variant="body1">{children}</HtmlTypography>
        ) : (
          <Typography variant="body1">{children}</Typography>
        )}
      </Box>
    </Dialog>
  );
};

interface UseConfirmDialogOptions {
  title: string;
  message?: string | (() => string) | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'neutral' | 'error';
  sx?: SxProps<Theme>;
}

const DEFAULT_CONFIRM_OPTIONS: UseConfirmDialogOptions = {
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmColor: 'error',
};

export const useConfirmDialogComponent = (
  initialOptions?: Partial<UseConfirmDialogOptions>
): UseConfirmDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<UseConfirmDialogOptions>({
    ...DEFAULT_CONFIRM_OPTIONS,
    ...initialOptions,
  });
  const [resolvePromise, setResolvePromise] = useState<
    ((value: boolean) => void) | null
  >(null);

  const confirm = useCallback(
    (
      updateOptions?: Partial<UseConfirmDialogOptions> | string
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        setOptions({
          ...DEFAULT_CONFIRM_OPTIONS,
          ...(typeof updateOptions === 'string'
            ? { message: updateOptions }
            : updateOptions),
        });
        setIsOpen(true);
        setResolvePromise(() => resolve);
      });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) resolvePromise(true);
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    if (resolvePromise) resolvePromise(false);
  }, [resolvePromise]);

  const dialogProps: DialogProps = {
    open: isOpen,
    title: options.title,
    children:
      typeof options.message === 'function'
        ? options.message()
        : options.message,
    actions: [
      {
        label: options.cancelText ?? 'Cancel',
        onClick: handleCancel,
        color: 'neutral',
      },
      {
        label: options.confirmText ?? 'Confirm',
        onClick: handleConfirm,
        color: options.confirmColor || 'error',
        variant: 'prominent',
      },
    ].filter((action) => !!action.label) as any,
    noWrap: false,
    sx: options.sx,
    onClose: handleCancel,
  };

  return [dialogProps, confirm];
};

export type UseConfirmDialogReturn = [
  DialogProps,
  (
    updateOptions?: Partial<UseConfirmDialogOptions> | string
  ) => Promise<boolean>,
];
